import React, { useCallback, useContext, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { CostPriceConversion, useGetConvertedCostPricesInAud } from '~/hooks/apis/fx/useGetConvertedCostPricesInAud';
import { useGetServiceCostPrices, useGetTourServices } from '~/hooks/apis/tours/useTourCostPrices';
import { useGetTourDepartures } from '~/hooks/apis/tours/useTourDepartures';
import { useGetDeparturesInventory } from '~/hooks/apis/tours/useTourInventory';

import { TourService } from '~/types/tours/costPrice';
import { costPriceTypeLabel, servicePricing } from '~/types/tours/supplier';

import LinkSupplierService from '../../Suppliers/LinkSupplierService';
import TourDetailsContext from '../TourDetailsContext';

import AllServiceCostPrices from './AllServiceCostPrices';
import DepartureCostPrices from './DepartureCostPrices';
import FixedPriceDialog from './FixedPriceDialog';
import FixedPriceTable from './FixedPriceTable';
import SlidingScaleTable from './SlidingScaleTable';
import SlidingScaleUploadDialog from './SlidingScaleUploadDialog';
import TourDepartureSelector from './TourDepartureSelector';
import TourInventorySection from './TourInventorySection';
import TourServiceOptionLabel from './TourServiceOptionLabel';
import { buildScaleAnnotation, fixedPricingAsScale, summariseDepartureCost } from './departureInventory';
import { DepartureItem, buildDepartureRows, groupDeparturesForSelector } from './departureRows';
import { formatAmount } from './formatAmount';
import { serviceCostPriceRows } from './serviceCostPriceRows';
import useInventorySnapshot from './useInventorySnapshot';

interface Props {
  tourId: string;
}

/** How a service is costed, since it decides what can be priced against it. */
function describeCosting(service: TourService | null): string {
  if (!service?.costPriceType) {
    return "Cost prices belong to a supplier's service on this tour";
  }

  const scope = service.costPriceScope === 'Departure' ? 'per departure' : 'for the whole tour';

  return `Costed as ${costPriceTypeLabel(service.costPriceType).toLowerCase()}, priced ${scope}`;
}

function Loading() {
  return (
    <Box p={4} display="flex" justifyContent="center">
      <CircularProgress />
    </Box>
  );
}

const SERVICE_ID_PARAM = 'serviceId';

export default function TourSlidingScale({ tourId }: Props) {
  const { tourDetails } = useContext(TourDetailsContext);

  const [searchParams, setSearchParams] = useSearchParams();

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isFixedPriceOpen, setIsFixedPriceOpen] = useState(false);

  /** Set when an upload was started from one departure's row. */
  const [uploadDeparture, setUploadDeparture] = useState<string | null>(null);

  const [isLinking, setIsLinking] = useState(false);

  const { data: services = [], isLoading: isLoadingServices, error: servicesError } = useGetTourServices(tourId);

  /**
   * The chosen service is kept in the URL so the view can be linked and
   * survives a reload. It defaults to the tour's only service when there is
   * just one, which is the common case.
   */
  const selectedServiceId = searchParams.get(SERVICE_ID_PARAM) ?? '';
  const isAllServices = selectedServiceId === '';

  const handleServiceChange = useCallback(
    (serviceId: string) => {
      const next = new URLSearchParams(searchParams);

      if (serviceId) {
        next.set(SERVICE_ID_PARAM, serviceId);
      } else {
        next.delete(SERVICE_ID_PARAM);
      }

      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const selectedService = useMemo(
    () => services.find((service) => service.id === selectedServiceId) ?? null,
    [services, selectedServiceId],
  );

  // Cost prices are keyed by supplier and service, both taken from the
  // selected service.
  const serviceIdentifier = useMemo(
    () => ({ supplierId: selectedService?.supplierId, serviceId: selectedService?.id }),
    [selectedService],
  );

  const { data, isLoading, error } = useGetServiceCostPrices(serviceIdentifier);

  /**
   * svc-tour returns one of four shapes, whichever matches how the service is
   * costed, so each is read out once and the rest stay empty.
   */
  const { pricing, fixedPrices, byDeparturePricing, fixedByDeparturePrices } = useMemo(
    () => serviceCostPriceRows(data),
    [data],
  );

  // With no service there is nothing to price, so those actions are left out
  // rather than shown disabled.
  const hasService = !!selectedService;

  const pricingActions = selectedService ? servicePricing(selectedService) : null;
  const isByDeparture = !!pricingActions?.isByDeparture;

  /**
   * A fixed service holds only fixed rows, so its own prices are what the
   * dialog opens on: the currency it quotes in, and the seasons it already
   * prices, so setting one more does not introduce "low" beside "Low".
   */
  const fixedForKind = isByDeparture ? fixedByDeparturePrices : fixedPrices;

  const fixedCurrency = fixedForKind[0]?.currency;

  const seasonOptions = useMemo(
    () => Array.from(new Set(fixedForKind.map((item) => item.season))).sort(),
    [fixedForKind],
  );

  /**
   * Departures are needed to price a by-departure service, and to choose which
   * departure's inventory to show for any sliding scale service.
   */
  const needsDepartures = !!pricingActions;

  const {
    data: tourDepartures = [],
    isLoading: isLoadingDepartures,
    error: departuresError,
  } = useGetTourDepartures(isAllServices || needsDepartures ? tourId : undefined);

  /**
   * The departure whose inventory is being looked at, held here rather than in
   * the inventory section because the price table above marks where it sits.
   *
   * Blank by default, and blank is a real choice rather than "not chosen yet":
   * this screen is the service's prices first, and someone reading the scale
   * should not have one departure's position drawn over it uninvited.
   */
  const [inventoryDepartureKey, setInventoryDepartureKey] = useState('');

  const sortedDepartures = useMemo(
    () => tourDepartures.slice().sort((a, b) => a.departureDate.localeCompare(b.departureDate)),
    [tourDepartures],
  );

  const departureGroups = useMemo(() => groupDeparturesForSelector(sortedDepartures), [sortedDepartures]);
  const inventoryDepartures = useMemo(
    () => departureGroups.find(([key]) => key === inventoryDepartureKey)?.[1] ?? [],
    [departureGroups, inventoryDepartureKey],
  );
  const inventoryDeparture = inventoryDepartures[0];

  const {
    data: departureInventory,
    isLoading: isLoadingInventory,
    error: inventoryError,
  } = useGetDeparturesInventory({
    serviceId: selectedService?.id,
    packageDepartureIds: inventoryDepartures.flatMap((departure) => departure.packageDepartureId ?? []),
  });

  const pricingForSelectedDeparture = useMemo(() => {
    if (!inventoryDepartureKey) {
      return pricing;
    }

    const season = departureInventory?.season;
    return season ? pricing.filter((item) => item.season.toLowerCase() === season.toLowerCase()) : [];
  }, [departureInventory?.season, inventoryDepartureKey, pricing]);

  const fixedPricesForSelectedDeparture = useMemo(() => {
    if (!inventoryDepartureKey) {
      return fixedPrices;
    }

    const season = departureInventory?.season;
    return season ? fixedPrices.filter((item) => item.season.toLowerCase() === season.toLowerCase()) : [];
  }, [departureInventory?.season, fixedPrices, inventoryDepartureKey]);

  const inventoryPricing = useMemo(() => {
    if (pricingActions?.kind === 'SlidingScale' || pricingActions?.kind === 'FixedPerGroup') {
      return pricingForSelectedDeparture;
    }

    const allocated = departureInventory?.entries.filter((entry) => entry.operation === 'allocated').length ?? 1;
    return fixedPricingAsScale(fixedPricesForSelectedDeparture, allocated);
  }, [departureInventory?.entries, fixedPricesForSelectedDeparture, pricingActions?.kind, pricingForSelectedDeparture]);

  const { snapshot, date: snapshotDate, onDateChange } = useInventorySnapshot(departureInventory);
  const scaleAnnotation = useMemo(
    () => buildScaleAnnotation(snapshot, inventoryDeparture?.departureName ?? '', pricingForSelectedDeparture),
    [snapshot, inventoryDeparture, pricingForSelectedDeparture],
  );
  const singleServiceCostSummary = useMemo(
    () => summariseDepartureCost(snapshot, inventoryPricing),
    [inventoryPricing, snapshot],
  );
  const singleServiceCostPrice = useMemo<Array<CostPriceConversion>>(
    () =>
      selectedService && inventoryDepartureKey
        ? [
            {
              serviceId: selectedService.id,
              amount: singleServiceCostSummary.totalCost,
              currency: singleServiceCostSummary.currency,
            },
          ]
        : [],
    [inventoryDepartureKey, selectedService, singleServiceCostSummary.currency, singleServiceCostSummary.totalCost],
  );
  const { convertedCostsByService, isLoadingByService } = useGetConvertedCostPricesInAud(singleServiceCostPrice);
  const convertedSingleServiceCost = selectedService ? convertedCostsByService[selectedService.id] : undefined;
  const isConvertingSingleServiceCost = selectedService ? isLoadingByService[selectedService.id] : false;

  /** A by-departure service holds one shape or the other, never both. */
  const departureRows = useMemo(() => {
    const items: Array<DepartureItem> =
      pricingActions?.kind === 'FixedByDeparture' ? fixedByDeparturePrices : byDeparturePricing;

    return buildDepartureRows(tourDepartures, items);
  }, [byDeparturePricing, fixedByDeparturePrices, pricingActions, tourDepartures]);

  return (
    <Stack spacing={2} sx={{ minWidth: 0, maxWidth: '100%' }}>
      <PageHeader title="Pricing and Inventory">
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {hasService ? (
            <>
              {/* A per-departure service is priced from its departure's own
                  row, where there is a departure to price. */}
              {!isByDeparture && pricingActions.canUploadSlidingScale && (
                <Button variant="contained" onClick={() => setIsUploadOpen(true)}>
                  {pricing.length ? 'Re-upload sliding scale' : 'Upload sliding scale'}
                </Button>
              )}
              {!isByDeparture && pricingActions.canSetFixedPrice && (
                <Button variant="contained" onClick={() => setIsFixedPriceOpen(true)}>
                  Set fixed price
                </Button>
              )}
              <Button variant="text" onClick={() => setIsLinking(true)}>
                Link another service
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={() => setIsLinking(true)}>
              Link supplier &amp; service
            </Button>
          )}
        </Stack>
      </PageHeader>

      {isLoadingServices && <Loading />}

      {!!servicesError && <Alert severity="error">{servicesError.message}</Alert>}

      {!isLoadingServices && !servicesError && !services.length && (
        <Alert severity="info">
          This tour has no supplier services yet, so there is nowhere to attach a cost price. Link a supplier and create
          a service for the tour to start pricing it.
        </Alert>
      )}

      {!!services.length && (
        <TextField
          select
          label="Service"
          value={selectedServiceId}
          onChange={(event) => handleServiceChange(event.target.value)}
          helperText={
            isAllServices ? 'Showing cost prices for all supplier services' : describeCosting(selectedService)
          }
          sx={{ maxWidth: 560 }}
          InputLabelProps={{ shrink: true }}
          SelectProps={{
            displayEmpty: true,
            renderValue: (value) => {
              if (!value) {
                return 'All';
              }

              const service = services.find((option) => option.id === value);
              return service ? <TourServiceOptionLabel service={service} /> : '';
            },
          }}
        >
          <MenuItem value="">All</MenuItem>
          {services.map((service) => (
            <MenuItem key={service.id} value={service.id}>
              <TourServiceOptionLabel service={service} />
            </MenuItem>
          ))}
        </TextField>
      )}

      {isAllServices && isLoadingDepartures && <Loading />}

      {isAllServices && !!departuresError && (
        <Alert severity="error">Could not load this tour&apos;s departures: {departuresError.message}</Alert>
      )}

      {isAllServices && !!services.length && !isLoadingDepartures && !departuresError && (
        <AllServiceCostPrices services={services} departures={sortedDepartures} />
      )}

      {!!selectedService && isLoading && <Loading />}

      {!!selectedService && !!error && <Alert severity="error">{error.message}</Alert>}

      {!!selectedService && !isLoading && !error && (
        <Stack spacing={1} sx={{ minWidth: 0 }}>
          {isByDeparture ? (
            <>
              {isLoadingDepartures && <Loading />}

              {/* Without the tour's departures there is nothing to price
                  against, so a failure is reported rather than read as a tour
                  that runs none. */}
              {!!departuresError && (
                <Alert severity="error">Could not load this tour&apos;s departures: {departuresError.message}</Alert>
              )}

              {!isLoadingDepartures && !departuresError && (
                <DepartureCostPrices
                  rows={departureRows}
                  heading={`Cost prices - ${selectedService.costPriceType === 'Fixed' ? 'Fixed' : 'Sliding Scale'}`}
                  kind={pricingActions.kind}
                  serviceId={selectedService.id}
                  onUpload={(departure) => {
                    setUploadDeparture(departure);

                    if (pricingActions.canSetFixedPrice) {
                      setIsFixedPriceOpen(true);
                      return;
                    }

                    setIsUploadOpen(true);
                  }}
                />
              )}
            </>
          ) : (
            /* A service is costed one way, so only that table is worth
               showing: the other's empty state describes a scale or a rate this
               service will never hold. */
            <>
              {!!(pricing.length || fixedPrices.length) && (
                <TourDepartureSelector
                  departures={sortedDepartures}
                  selectedDepartureKey={inventoryDepartureKey}
                  onSelectDeparture={setInventoryDepartureKey}
                />
              )}

              {!!inventoryDepartureKey && !!(pricing.length || fixedPrices.length) && (
                <Stack spacing={1}>
                  <Typography variant="h4" fontWeight={600}>
                    Cost summary
                  </Typography>
                  {isLoadingInventory ? (
                    <CircularProgress size={28} />
                  ) : (
                    <Paper variant="outlined" sx={{ p: 2, minWidth: 220, maxWidth: 320 }}>
                      <Typography variant="h4" fontWeight={600}>
                        {formatAmount(singleServiceCostSummary.totalCost)}{' '}
                        {singleServiceCostSummary.currency ?? 'No currency'}
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="h4" fontWeight={600}>
                        {isConvertingSingleServiceCost
                          ? 'Converting to AUD…'
                          : convertedSingleServiceCost === undefined
                            ? 'AUD unavailable'
                            : `$${formatAmount(convertedSingleServiceCost)} AUD`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {singleServiceCostSummary.units} travellers · {singleServiceCostSummary.singles} single
                        travellers
                      </Typography>
                      {!!singleServiceCostSummary.totalSingleSupplementCost && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Includes {formatAmount(singleServiceCostSummary.totalSingleSupplementCost)}{' '}
                          {singleServiceCostSummary.currency} in single supplements
                        </Typography>
                      )}
                    </Paper>
                  )}
                </Stack>
              )}

              <Typography variant="h4" fontWeight={600}>
                Cost prices - {selectedService.costPriceType === 'Fixed' ? 'Fixed' : 'Sliding Scale'}
              </Typography>

              {pricingActions.kind === 'Fixed' ? (
                <FixedPriceTable prices={fixedPricesForSelectedDeparture} />
              ) : (
                <SlidingScaleTable
                  pricing={pricingForSelectedDeparture}
                  annotation={scaleAnnotation}
                  showSingleSupplement={selectedService.costPriceType !== 'FixedPerGroup'}
                />
              )}

              {/* A whole-tour scale still prices real departures, so where a
                  departure has got to on it is worth showing here too. */}
              {!!inventoryDepartureKey && !!(pricing.length || fixedPrices.length) && (
                <Stack spacing={1} sx={{ minWidth: 0, pt: 2 }}>
                  <Typography variant="h4" fontWeight={600}>
                    Inventory
                  </Typography>

                  <TourInventorySection
                    departures={sortedDepartures}
                    selectedDepartureId={inventoryDepartureKey}
                    inventory={departureInventory}
                    snapshotDate={snapshotDate}
                    onDateChange={onDateChange}
                    items={inventoryPricing}
                    showCosts={false}
                    showCostChart
                    isLoading={isLoadingDepartures || isLoadingInventory}
                    error={departuresError ?? inventoryError}
                  />
                </Stack>
              )}
            </>
          )}
        </Stack>
      )}

      {isUploadOpen && !!selectedService && (
        <SlidingScaleUploadDialog
          open={isUploadOpen}
          service={selectedService}
          existingRowCount={isByDeparture ? byDeparturePricing.length : pricing.length}
          byDeparture={isByDeparture}
          tourId={tourId}
          // Clearing the dialog's own picker is what asks for a sheet spanning
          // several departures.
          departures={isByDeparture && uploadDeparture ? [uploadDeparture] : undefined}
          existingItems={isByDeparture ? byDeparturePricing : undefined}
          onClose={() => {
            setIsUploadOpen(false);
            setUploadDeparture(null);
          }}
        />
      )}

      {isFixedPriceOpen && !!selectedService && (
        <FixedPriceDialog
          open={isFixedPriceOpen}
          service={selectedService}
          defaultCurrency={fixedCurrency}
          seasonOptions={seasonOptions}
          byDeparture={isByDeparture}
          departure={tourDepartures.find((option) => option.departureId === uploadDeparture)}
          existingByDeparturePrices={fixedByDeparturePrices}
          onClose={() => {
            setIsFixedPriceOpen(false);
            setUploadDeparture(null);
          }}
        />
      )}

      {isLinking && (
        <LinkSupplierService
          tour={{ id: tourId, name: tourDetails?.title ?? tourId }}
          onClose={() => setIsLinking(false)}
          onCreated={(service) => {
            setIsLinking(false);
            handleServiceChange(service.id);
          }}
        />
      )}
    </Stack>
  );
}
